import { AxiosResponse } from 'axios';
import { Page } from '../models';
import { IncidentType } from '../models/incident.model';
import { Action } from '../models/action.model';
import { ActionSourceType } from '../models/action.model';
import { buildUrl } from '../utils';
import { httpClient } from './http.client';

// ── My assignments ────────────────────────────────────────────────────────────

export interface UserMyAssignmentsType {
  months: string[];
  actions: number[];
  inspections: number[];
  maintenances: number[];
  riskAssessments: number[];
}

export interface UserStatusTrend {
  months: string[];
  open: number[];
  overdue: number[];
  closed: number[];
}

export interface UserRaCompletionTrend {
  months: string[];
  completed: number[];
}

// ── Aggregated statistics response types ──────────────────────────────────────

export interface UserEventStatistics {
  total: number;
  countByType: Partial<Record<IncidentType, number>>;
}

export interface UserActionStatistics {
  openActions: number;
  overdueActions: number;
  closedActions: number;
  closedWithoutComplianceForm: number;
  reportedActions: number;
  reviews: number;
}

export interface UserEquipmentStatistics {
  inspection: {
    open: number;
    overdue: number;
    closed: number;
    reported: number;
  };
  maintenance: {
    open: number;
    overdue: number;
    closed: number;
    reported: number;
  };
}

export interface UserRiskAssessmentStatistics {
  open: number;
  overdue: number;
  closed: number;
  reported: number;
}

// ── Service calling /api/v1/user-statistics/* ─────────────────────────────────

class UserStatisticsService {
  getEventStatistics(): Promise<AxiosResponse<UserEventStatistics>> {
    return httpClient.get<UserEventStatistics>(buildUrl('user-statistics', ['events']));
  }

  getActionStatistics(): Promise<AxiosResponse<UserActionStatistics>> {
    return httpClient.get<UserActionStatistics>(buildUrl('user-statistics', ['actions']));
  }

  getEquipmentStatistics(): Promise<AxiosResponse<UserEquipmentStatistics>> {
    return httpClient.get<UserEquipmentStatistics>(buildUrl('user-statistics', ['equipment']));
  }

  getRiskAssessmentStatistics(): Promise<AxiosResponse<UserRiskAssessmentStatistics>> {
    return httpClient.get<UserRiskAssessmentStatistics>(buildUrl('user-statistics', ['risk-assessments']));
  }

  getMyAssignments(year: number): Promise<AxiosResponse<UserMyAssignmentsType>> {
    return httpClient.get<UserMyAssignmentsType>(buildUrl('user-statistics', ['my-assignments']), { params: { year } });
  }

  getMyWorkload(tab: string, year: number): Promise<AxiosResponse<UserStatusTrend>> {
    return httpClient.get<UserStatusTrend>(buildUrl('user-statistics', ['my-workload']), { params: { tab, year } });
  }

  getEarliestOpenYear(tab: string): Promise<AxiosResponse<number>> {
    return httpClient.get<number>(buildUrl('user-statistics', ['earliest-open-year']), { params: { tab } });
  }

  getRaCompletionTrend(): Promise<AxiosResponse<UserRaCompletionTrend>> {
    return httpClient.get<UserRaCompletionTrend>(buildUrl('user-statistics', ['ra-completion-trend']));
  }
}

export const userStatisticsService = new UserStatisticsService();

export interface InspectionListItem {
  id: number;
  name: string;
  type: string;
  targetType: string;
  nextInspection?: string;
  closed: boolean;
  overdue?: boolean;
  totalResults?: number;
  scheduled?: boolean;
  chatbotQuestionnaire?: { id: string | null; name: string };
  company?: string;
  division?: string;
}

export interface MaintenanceLogItem {
  id: number;
  name: string;
  status: string;
  dueDate?: string;
  createdAt?: string;
  equipment?: { id: number; name: string };
  responsibleUser?: { id: number; name: string; surname: string };
  creatorUser?: { id: number; fullName: string };
  chatbot?: { id: string | null; name: string };
  company?: string;
  division?: string;
}

export interface RiskAssessmentResultItem {
  id: number;
  name: string;
  type?: string;
  open?: string;
  dueDate?: string;
  lastCompletedDate?: string;
  responsibleUser?: { id: number; name: string; surname: string };
  responsibleUsers?: { id: number; fullName: string }[];
  chatbot?: { id: string | null; name: string };
  createdBy?: { id: number; fullName: string };
  tags?: { id: number | null; name: string }[];
  company?: string;
}


interface ActionFilterDTO {
  isLoggedInUserResponsible?: number[];
  status?: number[];
  sourceType?: ActionSourceType[];
  sort?: { fieldName: string; direction: string }[];
}

interface InspectionFilterDTO {
  isLoggedInUserResponsible?: number[];
  closed?: boolean[];
  sort?: { fieldName: string; direction: string }[];
}

interface MaintenanceFilterDTO {
  responsibleUserIds?: number[];
  status?: string[];
  sort?: { fieldName: string; direction: string }[];
}

interface RiskAssessmentFilterDTO {
  responsibleUser?: number[];
  open?: string[];
  isOverdue?: boolean;
  types?: string[];
  sort?: { fieldName: string; direction: string }[];
  companyIds?: number[];
}

const ALL_STATUSES = [0, 1, 2]; // OPEN=0, CLOSED=1, OVERDUE=2
const ALL_SOURCE_TYPES = [ActionSourceType.EVENT, ActionSourceType.RISK_ASSESSMENT];

export const OPEN_WORK_PAGE_SIZE = 10;

// Ant Design pages are 1-based; backend expects 0-based pageNumber
const toParams = (page: number) => ({ pageNumber: page - 1, pageSize: OPEN_WORK_PAGE_SIZE });

class MyWorkActivity {
  getMyActions(
    userId: number,
    page = 1,
    sort?: { fieldName: string; direction: string },
    statusFilter?: number[],
    sourceFilter?: ActionSourceType[]
  ): Promise<AxiosResponse<Page<Action>>> {
    const filter: ActionFilterDTO = {
      isLoggedInUserResponsible: [userId],
      status: statusFilter?.length ? statusFilter : ALL_STATUSES,
      sourceType: sourceFilter?.length ? sourceFilter : ALL_SOURCE_TYPES,
      sort: sort ? [sort] : undefined,
    };
    return httpClient.post<Page<Action>>(buildUrl('action', ['filter']), filter, { params: toParams(page) });
  }

  getMyInspections(
    userId: number,
    page = 1,
    sort?: { fieldName: string; direction: string },
    closed?: boolean[]
  ): Promise<AxiosResponse<Page<InspectionListItem>>> {
    const filter: InspectionFilterDTO = {
      isLoggedInUserResponsible: [userId],
      closed: closed ?? [true, false],
      sort: sort ? [sort] : [{ fieldName: 'closed', direction: 'ASC' }],
    };
    return httpClient.post<Page<InspectionListItem>>(buildUrl('inspection', ['filter']), filter, {
      params: toParams(page),
    });
  }

  getMyMaintenances(
    userId: number,
    page = 1,
    sort?: { fieldName: string; direction: string },
    statusFilter?: string[]
  ): Promise<AxiosResponse<Page<MaintenanceLogItem>>> {
    const filter: MaintenanceFilterDTO = {
      responsibleUserIds: [userId],
      status: statusFilter?.length ? statusFilter : ['OPEN', 'OVERDUE', 'DONE'],
      sort: sort ? [sort] : [{ fieldName: 'status', direction: 'ASC' }],
    };
    return httpClient.post<Page<MaintenanceLogItem>>(buildUrl('maintenance', ['filter']), filter, {
      params: toParams(page),
    });
  }

  getMyRiskAssessments(
    userId: number,
    page = 1,
    sort?: { fieldName: string; direction: string },
    typeFilter?: string[]
  ): Promise<AxiosResponse<Page<RiskAssessmentResultItem>>> {
    const filter: RiskAssessmentFilterDTO = {
      responsibleUser: [userId],
      types: typeFilter?.length ? typeFilter : ['RISK_ASSESSMENT', 'RISK_ASSESSMENT_RESULT'],
      sort: sort ? [{ fieldName: sort.fieldName, direction: sort.direction }] : undefined,
    };
    return httpClient.post<Page<RiskAssessmentResultItem>>(
      buildUrl('risk-assessments-and-results', ['filter']),
      filter,
      { params: toParams(page) }
    );
  }
}

export default new MyWorkActivity();
